import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, MailCheck } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const Shell = ({ children, testId }) => (
  <div className="min-h-screen flex items-center justify-center px-5 relative grain" data-testid={testId}>
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,#053671_0%,transparent_60%)] opacity-60" />
    <div className="relative w-full max-w-md card-surface rounded-3xl p-8 sm:p-10 reveal">
      <Link to="/" className="flex items-center gap-3 mb-8" data-testid={`${testId}-logo-link`}><Logo size={48} /><span className="font-display font-extrabold text-xl">Admin Portal</span></Link>
      {children}
    </div>
  </div>
);

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (err) { setError(errorMessage(err)); }
    finally { setLoading(false); }
  };

  return (
    <Shell testId="forgot-password-page">
      {sent ? (
        <div data-testid="forgot-password-sent">
          <MailCheck size={40} className="text-emerald-400" />
          <h1 className="mt-4 font-display text-2xl font-black tracking-tight">Check your inbox</h1>
          <p className="mt-2 text-sm text-slate-400">If <span className="text-white">{email}</span> has an account, a reset link is on its way. It works for one hour.</p>
          <Link to="/admin/login" className="mt-6 inline-flex items-center gap-2 text-sm text-sky-400 hover:text-sky-300" data-testid="forgot-password-back"><ArrowLeft size={14} /> Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit}>
          <h1 className="font-display text-3xl font-black tracking-tight">Forgot your password?</h1>
          <p className="mt-1 text-sm text-slate-400">Enter your login email and we'll send you a link to choose a new one.</p>
          <div className="mt-8"><Label htmlFor="email">Email</Label><Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 bg-white/5 border-white/10" data-testid="forgot-password-email-input" /></div>
          {error && <p className="mt-4 text-sm text-rose-400" data-testid="forgot-password-error">{error}</p>}
          <Button type="submit" disabled={loading} className="mt-6 w-full rounded-full bg-sky-400 text-[#070F1E] font-bold hover:bg-sky-300" data-testid="forgot-password-submit">{loading ? "Sending…" : "Send reset link"}</Button>
          <Link to="/admin/login" className="mt-5 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="forgot-password-back"><ArrowLeft size={14} /> Back to sign in</Link>
        </form>
      )}
    </Shell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return setError("Password must be at least 8 characters");
    if (password !== confirm) return setError("Passwords don't match");
    setError(""); setLoading(true);
    try { await api.post("/auth/reset-password", { token, password }); setDone(true); setTimeout(() => navigate("/admin/login"), 2500); }
    catch (err) { setError(errorMessage(err)); }
    finally { setLoading(false); }
  };

  return (
    <Shell testId="reset-password-page">
      {done ? (
        <div data-testid="reset-password-done">
          <CheckCircle2 size={40} className="text-emerald-400" />
          <h1 className="mt-4 font-display text-2xl font-black tracking-tight">Password updated</h1>
          <p className="mt-2 text-sm text-slate-400">Taking you to sign in…</p>
          <Link to="/admin/login" className="mt-6 inline-flex items-center gap-2 text-sm text-sky-400 hover:text-sky-300" data-testid="reset-password-login-link">Sign in now</Link>
        </div>
      ) : !token ? (
        <div data-testid="reset-password-missing"><h1 className="font-display text-2xl font-black tracking-tight">Invalid link</h1><p className="mt-2 text-sm text-slate-400">This reset link is missing its token.</p><Link to="/forgot-password" className="mt-6 inline-block text-sm text-sky-400 hover:text-sky-300">Request a new link</Link></div>
      ) : (
        <form onSubmit={submit}>
          <h1 className="font-display text-3xl font-black tracking-tight">Choose a new password</h1>
          <div className="mt-8 space-y-4">
            <div><Label htmlFor="password">New password</Label><div className="mt-1.5"><PasswordInput id="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-white/5 border-white/10" testId="reset-password-input" /></div></div>
            <div><Label htmlFor="confirm">Confirm password</Label><div className="mt-1.5"><PasswordInput id="confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="bg-white/5 border-white/10" testId="reset-password-confirm-input" /></div></div>
          </div>
          {error && <p className="mt-4 text-sm text-rose-400" data-testid="reset-password-error">{error}</p>}
          <Button type="submit" disabled={loading} className="mt-6 w-full rounded-full bg-sky-400 text-[#070F1E] font-bold hover:bg-sky-300" data-testid="reset-password-submit">{loading ? "Saving…" : "Set new password"}</Button>
        </form>
      )}
    </Shell>
  );
}
